/**
 * The pure half of the diagnostics control — Help → Package logs
 * (`docs/logging-and-observability.md` §4). The React hook that drives it is
 * `hooks/useDiagnostics.ts`; everything here takes the bridge as a value so it
 * can be tested with a stub.
 *
 * **Feature detection is the whole contract.** The web deploys separately from
 * the desktop (hard rule 6): against a v5 desktop `window.bimgo.diagnostics` is
 * `undefined`, and the right behaviour is for the control not to exist — not
 * to render and fail, and never to be stubbed. `detectDiagnostics` is the one
 * place that decides, and it demands the call itself, because a surface without
 * it is a bridge this build does not know.
 *
 * Every outcome is a *state to render* rather than an exception: the dialog
 * for a packaged zip, nothing at all for a cancelled save dialog, a toast for a
 * failure. The main process has already shown its own save dialog and revealed
 * the file in Explorer by the time `packageLogs` resolves; the renderer only
 * reports.
 */
import type { DiagnosticsBridge, PackagedLogs } from "../bimgo";

/** The minimal window shape the detection reads — `window` itself, or a stub. */
export interface DiagnosticsHost {
  bimgo?: { diagnostics?: Partial<DiagnosticsBridge> | undefined } | undefined;
}

/**
 * The bridge's diagnostics surface, or `null` when this desktop has none.
 *
 * Reads `packageLogs` as a function rather than trusting the object: a preload
 * that exposed a `diagnostics` object without it would otherwise pass detection
 * and fail on the click.
 */
export function detectDiagnostics(host: DiagnosticsHost | undefined): DiagnosticsBridge | null {
  const surface = host?.bimgo?.diagnostics;
  if (!surface) return null;
  if (typeof surface.packageLogs !== "function") return null;
  return surface as DiagnosticsBridge;
}

export type PackageOutcome =
  | { kind: "hidden" }
  | { kind: "cancelled" }
  | { kind: "packaged"; result: PackagedLogs }
  | { kind: "error"; message: string };

/**
 * Run `packageLogs` and classify what came back.
 *
 * A cancelled save dialog is the user changing their mind, not a failure — it
 * produces nothing to show. A rejected promise is the main process saying the
 * bundle could not be built (a staging dir it could not create, a zip it could
 * not write); its message is the one worth surfacing.
 */
export async function runPackageLogs(bridge: DiagnosticsBridge | null): Promise<PackageOutcome> {
  if (!bridge) return { kind: "hidden" };
  try {
    const result = await bridge.packageLogs();
    if (result.cancelled) return { kind: "cancelled" };
    return {
      kind: "packaged",
      result: {
        zipPath: result.zipPath,
        filesCollected: result.filesCollected,
        // Defensive: the type says it is always there, but a missing array must
        // not turn a successful bundle into a crashed dialog.
        skippedFiles: Array.isArray(result.skippedFiles) ? result.skippedFiles : [],
      },
    };
  } catch (error) {
    return { kind: "error", message: errorMessage(error) };
  }
}

export interface PackagedMessage {
  title: string;
  /** "N log files → path" */
  summary: string;
  /** What the zip holds and does not hold — the sentence the user reads before sending it. */
  contains: string;
  /** "N files were in use and skipped", or null when nothing was. */
  skipped: string | null;
}

/**
 * The result dialog's lines. Pure, so the composition — and in particular that
 * the skipped line appears only when something was skipped — has a test.
 */
export function describePackaged(
  result: PackagedLogs,
  t: (key: string, vars?: Record<string, string | number>) => string,
): PackagedMessage {
  return {
    title: t("settings.diagnostics.packaged"),
    summary: t("settings.diagnostics.packagedBody", { count: result.filesCollected, path: result.zipPath }),
    contains: t("settings.diagnostics.contains"),
    skipped:
      result.skippedFiles.length > 0
        ? t("settings.diagnostics.skipped", { count: result.skippedFiles.length })
        : null,
  };
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
